import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Textarea} from '@/components/ui/textarea'
import {ConfirmDialog} from '@/components/confirm-dialog'
import type {ActionNote} from '@/hooks/sgsst/plan/use-actions'
import {translateApiError} from '@/lib/errors'

const dateFormat = new Intl.DateTimeFormat('es-CO', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

type DeleteNoteMutation = {
  isPending: boolean
  mutate: (noteId: string, opts?: {onError?: (err: unknown) => void}) => void
}

/** Deleting confirms and handles its error here, so callers pass the raw `deleteNote` mutation;
 * adding a note never touches the action's estado. */
export function ActionNotesThread({
  notes,
  editable,
  adding,
  deleteNote,
  onAdd,
}: {
  notes: ActionNote[]
  editable: boolean
  adding: boolean
  deleteNote: DeleteNoteMutation
  onAdd: (text: string) => Promise<unknown>
}) {
  const {t} = useTranslation()
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null)

  async function submit() {
    if (!text.trim()) return
    setError(null)
    try {
      await onAdd(text.trim())
      setText('')
    } catch (err) {
      setError(translateApiError(err))
    }
  }

  return (
    <div className="space-y-2.5">
      {editable && (
        <div className="space-y-1.5">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={2000}
            minRows={2}
            placeholder={t('nota.placeholder')}
            data-testid="action-note-input"
          />
          <div className="flex items-center gap-2">
            <Button size="sm" data-testid="action-note-submit" disabled={adding || !text.trim()} onClick={submit}>
              {t('nota.add')}
            </Button>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        </div>
      )}
      {notes.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t('nota.empty')}</p>
      ) : (
        <ul className="space-y-2" data-testid="action-notes-list">
          {notes.map((note) => (
            <li key={note.id} className="rounded-lg border border-border px-3 py-2" data-testid="action-note">
              <div className="flex items-start justify-between gap-2">
                <p className="min-w-0 flex-1 text-sm break-words text-foreground">{note.text}</p>
                {note.can_delete && (
                  <button
                    type="button"
                    data-testid="action-note-delete"
                    onClick={() => setConfirmingDelete(note.id)}
                    className="shrink-0 text-xs text-muted-foreground hover:text-destructive">
                    {t('nota.remove')}
                  </button>
                )}
              </div>
              <p className="mt-1 font-mono text-[10px] text-muted-foreground">
                {note.author_name} {note.author_last_name} · {dateFormat.format(new Date(note.created_at))}
              </p>
            </li>
          ))}
        </ul>
      )}
      <ConfirmDialog
        open={!!confirmingDelete}
        onOpenChange={(open) => !open && setConfirmingDelete(null)}
        title={t('nota.deleteTitle')}
        description={t('nota.deleteBody')}
        confirmLabel={t('nota.remove')}
        cancelLabel={t('common.cancel')}
        pending={deleteNote.isPending}
        onConfirm={() => {
          if (confirmingDelete) {
            setDeleteError(null)
            deleteNote.mutate(confirmingDelete, {onError: (err) => setDeleteError(translateApiError(err))})
          }
          setConfirmingDelete(null)
        }}
      />
      {deleteError && (
        <p className="text-sm text-destructive" data-testid="action-note-delete-error">
          {deleteError}
        </p>
      )}
    </div>
  )
}
